import { observer } from 'mobx-react-lite';
import { Fragment } from 'react';

import { Icons } from '@/assets/icons/icons';
import { ModalsManager } from '@/components/modals/modalsManager';
import { Button } from '@/components/ui/Button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/Collapsible';
import { Grid, XStack, YStack } from '@/components/ui/layout/layout';
import { FeedbackModal } from '@/features/feedback/feedback';
import { Feedback } from '@/features/feedback/feedback.types';
import { getMeeting } from '@/features/meetings/meetings.state';
import { cn } from '@/lib/utils';

import {
  describeEvent,
  SOURCE_LABELS,
  type MeetingDebugCard as MeetingDebugCardData,
  type MeetingDebugTone,
} from '../meeting-debug.cards';

import { MeetingDebugChatButton } from './meeting-debug-chat-button';
import { MeetingDebugEventList } from './meeting-debug-event-list';

const TONE_TEXT: Record<MeetingDebugTone, string> = {
  ok: 'text-success-11',
  warning: 'text-warning-11',
  error: 'text-danger-11',
  neutral: 'text-foreground-muted',
};

const TONE_DOT: Record<MeetingDebugTone, string> = {
  ok: 'bg-success-9',
  warning: 'bg-warning-9',
  error: 'bg-danger-9',
  neutral: 'bg-foreground-subtle',
};

const SOURCE_TAG_CLASS: Record<
  MeetingDebugCardData['events'][number]['source'],
  string
> = {
  contextkit: 'bg-purple-500/10 text-purple-500',
  main: 'bg-blue-500/10 text-blue-500',
  renderer: 'bg-foreground/5 text-foreground-muted',
};

const formatTime = (epochMs: number) =>
  new Date(epochMs).toLocaleTimeString('en-US', {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

const formatClock = (epochMs: number) => formatTime(epochMs).slice(0, 5);

const formatDuration = (ms: number) => {
  const minutes = Math.round(ms / 60_000);
  if (minutes < 1) return `${Math.round(ms / 1000)}s`;
  if (minutes < 60) return `${minutes}m`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
};

export const MeetingDebugCard = observer(
  ({
    card,
    defaultOpen = false,
  }: {
    card: MeetingDebugCardData;
    defaultOpen?: boolean;
  }) => {
    const title =
      (card.meetingId && getMeeting(card.meetingId)?.name) || card.title;
    const timeRange = card.endedAt
      ? `${formatClock(card.startedAt)} – ${formatClock(card.endedAt)} · ${formatDuration(card.endedAt - card.startedAt)}`
      : formatClock(card.startedAt);

    return (
      <YStack
        role="article"
        aria-label={title}
        className="rounded-lg border border-border bg-surface"
      >
        <YStack className="gap-3 px-4 py-3">
          <XStack className="items-center justify-between gap-3">
            <XStack className="items-center gap-2 min-w-0">
              <span
                className={cn(
                  'size-2 shrink-0 rounded-full',
                  TONE_DOT[card.tone]
                )}
              />
              <span className="font-medium text-foreground truncate">
                {title}
              </span>
            </XStack>
            <XStack className="items-center gap-2 shrink-0">
              <span className="text-xs text-foreground-muted tabular-nums">
                {timeRange}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  ModalsManager.show(FeedbackModal, {
                    initialFeedbackType: Feedback.Bug,
                    meetingDebug: { card, title },
                  })
                }
              >
                Share
              </Button>
              <MeetingDebugChatButton card={card} title={title} />
            </XStack>
          </XStack>
          {card.verdicts.length > 0 && (
            <Grid className="grid-cols-[8rem_1fr] gap-x-3 gap-y-1 text-sm">
              {card.verdicts.map((verdict) => (
                <Fragment key={verdict.label}>
                  <span className="text-foreground-muted">{verdict.label}</span>
                  <span className={TONE_TEXT[verdict.tone]}>
                    {verdict.detail}
                  </span>
                </Fragment>
              ))}
            </Grid>
          )}
        </YStack>
        <Collapsible defaultOpen={defaultOpen}>
          <CollapsibleTrigger className="flex items-center gap-1.5 w-full px-4 py-2 border-t border-border text-xs text-foreground-muted group">
            <Icons.chevronRight className="size-3 transition-transform group-data-[state=open]:rotate-90" />
            Steps ({card.events.length})
          </CollapsibleTrigger>
          <CollapsibleContent>
            <YStack className="gap-3 px-4 pb-3">
              <YStack role="list" className="gap-1">
                {card.events.map((event, index) => (
                  <XStack
                    key={`${event.at}-${index}`}
                    role="listitem"
                    className="items-baseline gap-3 text-sm"
                  >
                    <span className="text-xs font-mono tabular-nums text-foreground-muted shrink-0">
                      {formatTime(event.at)}
                    </span>
                    <span
                      className={cn(
                        'w-[4.75rem] shrink-0 rounded px-1.5 py-px text-center text-[0.6875rem] font-medium',
                        SOURCE_TAG_CLASS[event.source]
                      )}
                    >
                      {SOURCE_LABELS[event.source]}
                    </span>
                    <span className="text-foreground">
                      {describeEvent(event)}
                    </span>
                  </XStack>
                ))}
              </YStack>
              <Collapsible>
                <CollapsibleTrigger className="flex items-center gap-1.5 text-xs text-foreground-muted group">
                  <Icons.chevronRight className="size-3 transition-transform group-data-[state=open]:rotate-90" />
                  Raw entries
                </CollapsibleTrigger>
                <CollapsibleContent className="pt-2">
                  <MeetingDebugEventList
                    events={card.events}
                    emptyLabel="No entries."
                  />
                </CollapsibleContent>
              </Collapsible>
            </YStack>
          </CollapsibleContent>
        </Collapsible>
      </YStack>
    );
  }
);
